import { useEffect, useMemo, useRef, type JSX } from 'react';
import {
  Controller,
  type DefaultValues,
  type FieldValues,
  type Path,
  type Resolver,
  useForm,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { HelpCircle } from 'lucide-react';
import type { ZodTypeAny } from 'zod';

import {
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { DatePicker } from '@/components/ui/date-picker';
import type { FieldConfig } from '@/components/blocks/forms';
import { FeatureErrorBoundary } from '@/components/blocks/feedback/FeatureErrorBoundary';
import { cn } from '@/lib/utils';
import { formatDateToItDateInput, parseItDateInputToDate } from '@/utils/date';

const DEFAULT_SCOPE_MESSAGE =
  'Le modifiche si applicheranno a questo report e a tutti i report futuri.';

interface EntityFieldModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  contextMessage: string;
  fields: readonly FieldConfig[];
  schema: ZodTypeAny;
  defaultValues: Record<string, unknown>;
  onSave: (values: Record<string, unknown>) => Promise<void>;
  isSaving?: boolean;
}

export function EntityFieldModal({
  open,
  onOpenChange,
  title,
  contextMessage,
  fields,
  schema,
  defaultValues,
  onSave,
  isSaving = false,
}: EntityFieldModalProps): JSX.Element {
  const bypassDirtyRef = useRef(false);

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<FieldValues>({
    resolver: zodResolver(schema as never) as unknown as Resolver<FieldValues>,
    defaultValues: defaultValues as DefaultValues<FieldValues>,
    mode: 'onBlur',
  });

  const watchedValues = watch() as FieldValues;

  useEffect(() => {
    if (!open) return;
    reset(defaultValues as DefaultValues<FieldValues>);
  }, [defaultValues, open, reset]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      onOpenChange(true);
      return;
    }

    if (bypassDirtyRef.current) {
      bypassDirtyRef.current = false;
      onOpenChange(false);
      return;
    }

    if (isDirty) {
      const confirmed = window.confirm(
        'Ci sono modifiche non salvate. Vuoi chiudere senza salvare?'
      );
      if (!confirmed) return;
    }

    onOpenChange(false);
  };

  const handleSave = handleSubmit(async (formData) => {
    try {
      await onSave(formData);
      bypassDirtyRef.current = true;
      handleOpenChange(false);
    } catch (error) {
      const field =
        error &&
        typeof error === 'object' &&
        typeof (error as { field?: unknown }).field === 'string'
          ? ((error as { field: string }).field as Path<FieldValues>)
          : null;

      if (field) {
        setError(field, {
          type: 'manual',
          message: (error as Error).message,
        });
        return;
      }

      setError('root' as Path<FieldValues>, {
        type: 'manual',
        message: error instanceof Error ? error.message : 'Operazione non riuscita. Riprova.',
      });
    }
  });

  const fieldList = useMemo(
    () =>
      fields.map((field) => {
        const fieldPath = field.key as Path<FieldValues>;
        const error = errors[fieldPath];
        const rawValue = watchedValues[fieldPath];
        const value = rawValue ?? '';
        const isRequired = field.required;
        const isDisabled =
          typeof field.disabled === 'function'
            ? Boolean(field.disabled(watchedValues))
            : Boolean(field.disabled);

        const errorInputClasses = error ? 'border-destructive' : '';

        return (
          <div
            key={field.key}
            className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-4 border-b border-[var(--border-subtle)] py-3 last:border-b-0"
          >
            <div className="flex items-center gap-1 text-sm font-medium text-text-secondary">
              <Label htmlFor={field.key}>
                {field.label}
                {isRequired && <span className="ml-1 text-destructive">*</span>}
              </Label>
              {field.tooltip && (
                <Tooltip>
                  <TooltipTrigger type="button" className="cursor-help">
                    <HelpCircle className="h-4 w-4 text-text-secondary" aria-hidden />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    {field.tooltip}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            <div className="flex min-h-[2.5rem] items-center">
              <div className="w-full">
                {field.type === 'textarea' ? (
                  <Textarea
                    id={field.key}
                    {...register(fieldPath)}
                    placeholder={field.placeholder}
                    disabled={isDisabled}
                    className={cn(errorInputClasses, 'resize-none')}
                    rows={4}
                  />
                ) : field.type === 'date' ? (
                  <DatePicker
                    date={value ? parseItDateInputToDate(String(value)) : undefined}
                    onSelect={(date) => {
                      const formattedDate = date ? formatDateToItDateInput(date) : '';
                      setValue(fieldPath, formattedDate);
                    }}
                    placeholder={field.placeholder || 'Seleziona data'}
                    className="w-full"
                    disabled={isDisabled}
                  />
                ) : field.type === 'checkbox' ? (
                  <div className="flex items-center h-10">
                    <Controller
                      control={control}
                      name={fieldPath}
                      render={({ field: { value, onChange } }) => (
                        <Checkbox
                          id={field.key}
                          checked={Boolean(value)}
                          onCheckedChange={onChange}
                          disabled={isDisabled}
                        />
                      )}
                    />
                  </div>
                ) : field.type === 'select' ? (
                  <Controller
                    control={control}
                    name={fieldPath}
                    render={({ field: { value, onChange } }) => (
                      <Select
                        onValueChange={onChange}
                        value={String(value || '')}
                        disabled={isDisabled}
                      >
                        <SelectTrigger id={field.key} className={cn(errorInputClasses)}>
                          <SelectValue placeholder={field.placeholder ?? "Seleziona un'opzione"} />
                        </SelectTrigger>
                        <SelectContent>
                          {field.options?.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                ) : (
                  <Input
                    id={field.key}
                    type={field.type}
                    {...register(fieldPath)}
                    placeholder={field.placeholder}
                    disabled={isDisabled}
                    className={cn(errorInputClasses)}
                  />
                )}

                {error && (
                  <p className="mt-1 text-sm text-destructive">{error.message?.toString()}</p>
                )}

                {field.description && (
                  <p className="mt-1 text-xs text-text-secondary">{field.description}</p>
                )}
              </div>
            </div>
          </div>
        );
      }),
    [control, errors, fields, register, setValue, watchedValues]
  );

  const rootError = (errors as unknown as { root?: { message?: unknown } }).root?.message;

  const formattedContextMessage = contextMessage.trim();
  const formattedScopeMessage = DEFAULT_SCOPE_MESSAGE;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogCloseButton />
        </DialogHeader>

        <DialogBody className="space-y-4">
          <FeatureErrorBoundary
            featureName="Modale"
            fallbackTitle="Errore nel modulo"
            fallbackMessage="Si è verificato un errore nel contenuto della finestra. Chiudi e riprova."
            onClose={() => onOpenChange(false)}
            closeLabel="Chiudi"
          >
            <Alert variant="info" showIcon>
              <AlertTitle>Contesto</AlertTitle>
              <AlertDescription>
                {formattedContextMessage ? <p>{formattedContextMessage}</p> : null}
                <p>{formattedScopeMessage}</p>
              </AlertDescription>
            </Alert>

            <div className="flex flex-col">{fieldList}</div>

            {rootError ? (
              <div className="rounded-md border border-status-error bg-error-100/40 px-3 py-2 text-sm text-status-error">
                {String(rootError)}
              </div>
            ) : null}
          </FeatureErrorBoundary>
        </DialogBody>

        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting || isSaving}
          >
            Annulla
          </Button>
          <Button onClick={handleSave} disabled={isSubmitting || isSaving}>
            {isSubmitting || isSaving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
